import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { addPatient, searchPatients, updatePatient } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  AlertTriangle, 
  UserPlus,
  Loader2,
  Users,
  CheckCircle,
  Stethoscope,
  X,
  Save,
  Heart,
  MapPin,
  CreditCard,
  Edit,
  Eye,
  UserCheck
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface FormData {
  name: string;
  age: string;
  date_of_birth: string;
  gender: string;
  phone: string;
  phone_2: string;
  email: string;
  address: string;
  city: string;
  pin_code: string;
  preferred_language: string;
  aadhar_number: string;
  blood_group: string;
  allergies: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  payment_preference: string;
}

interface ExistingPatient {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  email?: string;
}

interface DuplicateInfo {
  type: 'name' | 'phone';
  patients: ExistingPatient[];
}

interface PotentialDuplicate {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  email?: string;
  match_type: 'exact_name' | 'exact_phone' | 'similar_name';
  similarity_score?: number;
}

interface SmartDuplicateCheck {
  has_duplicates: boolean;
  duplicates: PotentialDuplicate[];
  can_proceed: boolean;
  message?: string;
}

type SubmitIntent = 'patient-only' | 'patient-and-rx' | 'patient-and-bill' | 'patient-and-queue';

interface PatientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newPatient: any, intent: SubmitIntent) => void;
  title?: string;
  showPrescriptionOption?: boolean;
  showQueueOption?: boolean;
  mode?: 'create' | 'edit';
  initialData?: Partial<FormData>;
  patientId?: string;
  compact?: boolean;
}

export default function PatientFormModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  title = "Add New Patient",
  showPrescriptionOption = true,
  showQueueOption = false,
  mode = 'create',
  initialData,
  patientId,
  compact = true
}: PatientFormModalProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isDoctor = currentUser?.role?.toLowerCase() === "doctor";

  const [formData, setFormData] = useState<FormData>({
    name: "",
    age: "",
    date_of_birth: "",
    gender: "Male",
    phone: "",
    phone_2: "",
    email: "",
    address: "",
    city: "",
    pin_code: "",
    preferred_language: "English",
    aadhar_number: "",
    blood_group: "",
    allergies: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    payment_preference: "Cash"
  });
  
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [duplicateCheck, setDuplicateCheck] = useState<SmartDuplicateCheck | null>(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
  const [userConfirmedDuplicate, setUserConfirmedDuplicate] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        age: "",
        date_of_birth: "",
        gender: "Male",
        phone: "",
        phone_2: "",
        email: "",
        address: "",
        city: "",
        pin_code: "",
        preferred_language: "English",
        aadhar_number: "",
        blood_group: "",
        allergies: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        payment_preference: "Cash"
      });
      setValidationErrors({});
      setDuplicateCheck(null);
      setCheckingDuplicates(false);
      setShowDuplicateDetails(false);
      setUserConfirmedDuplicate(false);
    }
  }, [isOpen]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && mode === 'edit' && initialData) {
      setFormData({
        name: initialData.name || "",
        age: initialData.age?.toString() || "",
        date_of_birth: initialData.date_of_birth || "",
        gender: initialData.gender || "Male",
        phone: initialData.phone || "",
        phone_2: initialData.phone_2 || "",
        email: initialData.email || "",
        address: initialData.address || "",
        city: initialData.city || "",
        pin_code: initialData.pin_code || "",
        preferred_language: initialData.preferred_language || "English",
        aadhar_number: initialData.aadhar_number || "",
        blood_group: initialData.blood_group || "",
        allergies: initialData.allergies || "",
        emergency_contact_name: initialData.emergency_contact_name || "",
        emergency_contact_phone: initialData.emergency_contact_phone || "",
        payment_preference: initialData.payment_preference || "Cash"
      });
    }
  }, [isOpen, mode, initialData]);

  // Smart duplicate checking - Skip in edit mode
  useEffect(() => {
    const performSmartDuplicateCheck = async () => {
      // Skip duplicate checking in edit mode since we're editing the same patient
      if (mode === 'edit') {
        return;
      }

      if (!formData.name.trim() || !formData.phone.trim()) {
        setDuplicateCheck(null);
        return;
      }

      setCheckingDuplicates(true);
      setDuplicateCheck(null);

      try {
        const searchResults = await searchPatients(formData.name.trim(), currentUser?.clinic_id);
        
        if (!searchResults || !Array.isArray(searchResults) || searchResults.length === 0) {
          setDuplicateCheck(null);
          return;
        }

        const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
        const inputPhone = normalizePhone(formData.phone);
        const inputName = formData.name.trim().toLowerCase();

        const duplicates: PotentialDuplicate[] = [];

        searchResults.forEach((patient: any) => {
          if (patient.phone) {
            const patientPhone = normalizePhone(patient.phone);
            if (patientPhone === inputPhone && patientPhone.length === 10) {
              duplicates.push({
                ...patient,
                match_type: 'exact_phone'
              });
              return;
            }
          }

          const patientName = patient.name.toLowerCase();
          if (patientName === inputName) {
            duplicates.push({
              ...patient,
              match_type: 'exact_name'
            });
            return;
          }

          const similarity = calculateNameSimilarity(inputName, patientName);
          if (similarity >= 0.8) {
            duplicates.push({
              ...patient,
              match_type: 'similar_name',
              similarity_score: similarity
            });
          }
        });

        // Remove exact duplicates by ID
        const uniqueDuplicates = duplicates.filter((duplicate, index, self) => 
          index === self.findIndex(d => d.id === duplicate.id)
        );

        if (uniqueDuplicates.length > 0) {
          // Sort by match priority: exact_phone > exact_name > similar_name
          uniqueDuplicates.sort((a, b) => {
            const priority = { 'exact_phone': 3, 'exact_name': 2, 'similar_name': 1 };
            return priority[b.match_type] - priority[a.match_type];
          });

          let message = "Similar patients found";
          if (uniqueDuplicates.some(d => d.match_type === 'exact_phone')) {
            message = "Phone number already exists";
          } else if (uniqueDuplicates.some(d => d.match_type === 'exact_name')) {
            message = "Similar name found";
          }

          setDuplicateCheck({
            has_duplicates: true,
            duplicates: uniqueDuplicates,
            can_proceed: true,
            message
          });
        } else {
          setDuplicateCheck(null);
        }

      } catch (error) {
        console.error('Error checking duplicates:', error);
        setDuplicateCheck(null);
      } finally {
        setCheckingDuplicates(false);
      }
    };

    const debounceTimer = setTimeout(performSmartDuplicateCheck, 600);
    return () => clearTimeout(debounceTimer);
  }, [formData.name, formData.phone, mode]);

  // Simple name similarity calculation
  const calculateNameSimilarity = (name1: string, name2: string): number => {
    const normalize = (str: string) => str.toLowerCase().replace(/[^a-z\s]/g, '').trim();
    const n1 = normalize(name1);
    const n2 = normalize(name2);
    
    if (n1 === n2) return 100;
    
    const words1 = n1.split(/\s+/);
    const words2 = n2.split(/\s+/);
    
    let matches = 0;
    words1.forEach(word1 => {
      if (words2.some(word2 => word1.includes(word2) || word2.includes(word1))) {
        matches++;
      }
    });
    
    return Math.round((matches / Math.max(words1.length, words2.length)) * 100);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Patient name is required
    if (!formData.name.trim()) {
      errors.name = "Patient name is required";
    }

    // Phone number is required
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone number must be exactly 10 digits";
    }

    // Age or Date of Birth is required
    if (!formData.age.trim() && !formData.date_of_birth.trim()) {
      errors.age = "Age or Date of Birth is required";
    } else if (formData.age && (isNaN(parseInt(formData.age)) || parseInt(formData.age) < 0 || parseInt(formData.age) > 120)) {
      errors.age = "Please enter a valid age (0-120)";
    }

    // Gender is required (already defaults to "Male", so this is just for safety)
    if (!formData.gender) {
      errors.gender = "Gender is required";
    }

    // Optional field validations
    if (formData.pin_code && !/^\d{6}$/.test(formData.pin_code)) {
      errors.pin_code = "PIN code must be exactly 6 digits";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (intent: SubmitIntent) => {
    if (!validateForm()) {
      toast({ title: "Please fix the validation errors", variant: "destructive" });
      return;
    }

    // Check if user needs to confirm duplicates
    if (mode === 'create' && duplicateCheck?.has_duplicates && !userConfirmedDuplicate) {
      toast({
        title: "Please Confirm",
        description: "Similar patients found. Please review and confirm this is a different person.",
        variant: "default"
      });
      setShowDuplicateDetails(true);
      return;
    }

    setLoading(true);

    try {
      const patientData = {
        name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : null,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender,
        phone: formData.phone.trim(),
        phone_2: formData.phone_2.trim() || null,
        email: formData.email.trim() || null,
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        pin_code: formData.pin_code.trim() || null,
        preferred_language: formData.preferred_language,
        aadhar_number: formData.aadhar_number.trim() || null,
        blood_group: formData.blood_group || null,
        allergies: formData.allergies.trim() || null,
        emergency_contact_name: formData.emergency_contact_name.trim() || null,
        emergency_contact_phone: formData.emergency_contact_phone.trim() || null,
        payment_preference: formData.payment_preference,
        doctor_id: currentUser?.doctor_id,
        clinic_id: currentUser?.clinic_id
      };

      let result;
      if (mode === 'edit' && patientId) {
        const response = await updatePatient(patientId, patientData);
        console.log(response);
        if (!response?.success) {
          throw new Error("Failed to update patient");
        }
        
        result = { id: patientId, ...patientData, updated_at: new Date().toISOString() };
        toast({ title: "Patient Updated Successfully", description: `${formData.name}'s details have been updated.` });
      } else {
        const apiResponse = await addPatient(patientData);
        if (!apiResponse || !apiResponse.id) {
          throw new Error("Failed to create patient - no ID returned from server");
        }

        result = {
          id: apiResponse.id,
          ...patientData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const intentText = intent === 'patient-and-rx' ? 'and prescription ready' : 
                          intent === 'patient-and-bill' ? 'and bill ready' : 
                          intent === 'patient-and-queue' ? 'and added to queue' : 'successfully';
        toast({ title: "Patient Added Successfully", description: `${formData.name} has been registered ${intentText}.` });
      }
      
      // Handle prescription navigation
      if (intent === 'patient-and-rx') {
        onClose();
        // Navigate to patient page with prescription modal open
        navigate(`/patients/${result.id}?openPrescription=true`);
        return;
      }

      // patient-and-queue: no navigation, just callback and close
      if (intent === 'patient-and-queue') {
        onSuccess?.(result, intent);
        onClose();
        return;
      }
      
      onSuccess?.(result, intent);
      onClose();
      
    } catch (error) {
      console.error(`Failed to ${mode} patient:`, error);
      toast({ title: `Failed to ${mode} patient`, description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDuplicate = () => {
    setUserConfirmedDuplicate(true);
    setShowDuplicateDetails(false);
    toast({
      title: "Confirmed",
      description: "You can now proceed to add this as a new patient.",
      variant: "default"
    });
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
    // Reset confirmation when key fields change
    if ((field === 'name' || field === 'phone') && userConfirmedDuplicate) {
      setUserConfirmedDuplicate(false);
    }
  };

  const getMatchTypeIcon = (type: string) => {
    switch (type) {
      case 'exact_phone': return <Phone className="w-4 h-4 text-red-500" />;
      case 'exact_name': return <User className="w-4 h-4 text-orange-500" />;
      case 'similar_name': return <Users className="w-4 h-4 text-amber-500" />;
      default: return <AlertTriangle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getMatchTypeBadge = (type: string) => {
    switch (type) {
      case 'exact_phone': return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full dark:bg-red-900/30 dark:text-red-300">Phone Match</span>;
      case 'exact_name': return <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full dark:bg-orange-900/30 dark:text-orange-300">Name Match</span>;
      case 'similar_name': return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900/30 dark:text-blue-300">Similar</span>;
      default: return null;
    }
  };

  // Enhanced canSubmit validation for all mandatory fields
  const canSubmit = !loading && 
                   !checkingDuplicates && 
                   formData.name.trim() && 
                   formData.phone.trim() && 
                   formData.gender && 
                   (formData.age.trim() || formData.date_of_birth.trim());

  const GenderButton = ({ value, label }: { value: string; label: string }) => (
    <button
      type="button"
      onClick={() => updateFormData('gender', value)}
      className={cn(
        "px-6 py-2 text-sm font-medium rounded-lg border transition-colors",
        formData.gender === value
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-foreground border-border hover:bg-muted"
      )}
    >
      {label}
    </button>
  );

  return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col overflow-hidden">
          {/* Fixed Header */}
          <DialogHeader className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                {mode === 'edit' ? (
                    <Edit className="w-4 h-4 text-primary" />
                ) : (
                    <UserPlus className="w-4 h-4 text-primary" />
                )}
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  {mode === 'edit' ? 'Update Patient Information' : (title || 'Patient Registration')}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  {mode === 'edit' ? 'Modify patient details and save changes' : 'Add new patient to the system'}
                </DialogDescription>
              </div>
              {checkingDuplicates && mode === 'create' && (
                  <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking...
                  </div>
              )}
            </div>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Smart Duplicate Alert */}
            {mode === 'create' && duplicateCheck?.has_duplicates && (
                <div className={cn(
                    "mx-6 mt-3 mb-2 p-2.5 rounded-lg border text-sm",
                    userConfirmedDuplicate
                        ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800/30"
                        : "bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800/30"
                )}>
              <div className="flex items-center gap-2 text-sm">
                {userConfirmedDuplicate ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                )}
                <span className={cn(
                  "font-medium",
                  userConfirmedDuplicate
                    ? "text-green-800 dark:text-green-200"
                    : "text-amber-800 dark:text-amber-200"
                )}>
                  {userConfirmedDuplicate 
                    ? "Confirmed: Adding as new patient"
                    : `${duplicateCheck.message} - Review and confirm if different person`
                  }
                </span>
                {!userConfirmedDuplicate && (
                  <>
                    {!showDuplicateDetails ? (
                      <button
                        onClick={() => setShowDuplicateDetails(true)}
                        className="ml-auto text-xs hover:underline text-amber-700 dark:text-amber-300"
                      >
                        View details
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowDuplicateDetails(false)}
                        className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
              
              {showDuplicateDetails && !userConfirmedDuplicate && (
                <div className="mt-2 pt-2 border-t border-current/20">
                  <div className="text-xs font-medium mb-1 text-amber-700 dark:text-amber-300">
                    Similar Patients Found:
                  </div>
                  <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
                    {duplicateCheck.duplicates.map((patient) => (
                      <div key={patient.id} className="bg-background/60 rounded p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="font-medium">{patient.name}</span>
                            <span>{patient.phone || 'N/A'}</span>
                            <span>Age: {patient.age || 'N/A'}</span>
                            <span>Gender: {patient.gender || 'N/A'}</span>
                          </div>
                          {getMatchTypeBadge(patient.match_type)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowDuplicateDetails(false)}
                      className="text-xs h-7"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleConfirmDuplicate}
                      className="text-xs h-7 bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Confirm Different Person
                    </Button>
                  </div>
                </div>
              )}
                </div>
            )}

            {/* Form Content */}
            <div className="px-6 py-4 space-y-3.5">
              {/* Row 1: Name and Phone */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Name */}
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <User className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <Label className="text-sm font-medium text-foreground">
                      Name <span className="text-red-500 text-xs">*</span>
                    </Label>
                  </div>
                  <div className="relative flex-1">
                    <Input
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        placeholder="Enter patient name"
                        className={cn(
                            "h-9",
                            validationErrors.name && "border-destructive",
                            mode === 'create' && duplicateCheck?.has_duplicates && duplicateCheck.duplicates.some(d => d.match_type === 'exact_name') && !userConfirmedDuplicate && "border-amber-400"
                        )}
                    />
                    {checkingDuplicates && mode === 'create' && (
                        <Loader2 className="absolute right-2.5 top-2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {validationErrors.name && (
                        <p className="text-xs text-destructive mt-1">{validationErrors.name}</p>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <Phone className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <Label className="text-sm font-medium text-foreground">
                      Phone <span className="text-red-500 text-xs">*</span>
                    </Label>
                  </div>
                  <div className="relative flex-1">
                    <Input
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit mobile"
                        maxLength={10}
                        className={cn(
                            "h-9",
                            validationErrors.phone && "border-destructive",
                            mode === 'create' && duplicateCheck?.has_duplicates && duplicateCheck.duplicates.some(d => d.match_type === 'exact_phone') && !userConfirmedDuplicate && "border-amber-400"
                        )}
                    />
                    {checkingDuplicates && mode === 'create' && (
                        <Loader2 className="absolute right-2.5 top-2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {validationErrors.phone && (
                        <p className="text-xs text-destructive mt-1">{validationErrors.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Gender and Age/DOB */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Gender */}
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-1.5">
                    <Label className="text-sm font-medium text-foreground">
                      Gender <span className="text-red-500 text-xs">*</span>
                    </Label>
                  </div>
                  <div className="flex gap-2 flex-1">
                    <button
                        type="button"
                        onClick={() => updateFormData('gender', 'Male')}
                        className={cn(
                            "flex-1 px-3 py-1.5 text-sm font-medium rounded-md border transition-all",
                            formData.gender === 'Male'
                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                : "bg-background text-foreground border-border hover:bg-accent"
                        )}
                    >
                      Male
                    </button>
                    <button
                        type="button"
                        onClick={() => updateFormData('gender', 'Female')}
                        className={cn(
                            "flex-1 px-3 py-1.5 text-sm font-medium rounded-md border transition-all",
                            formData.gender === 'Female'
                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                : "bg-background text-foreground border-border hover:bg-accent"
                        )}
                    >
                      Female
                    </button>
                    <button
                        type="button"
                        onClick={() => updateFormData('gender', 'Other')}
                        className={cn(
                            "flex-1 px-3 py-1.5 text-sm font-medium rounded-md border transition-all",
                            formData.gender === 'Other'
                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                : "bg-background text-foreground border-border hover:bg-accent"
                        )}
                    >
                      Other
                    </button>
                  </div>
                </div>

                {/* Age or DOB */}
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <Calendar className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <Label className="text-sm font-medium text-foreground">
                      Age/DOB <span className="text-red-500 text-xs">*</span>
                    </Label>
                  </div>
                  <div className="flex gap-2 flex-1">
                    <Input
                        type="number"
                        value={formData.age}
                        onChange={(e) => updateFormData('age', e.target.value)}
                        placeholder="Age"
                        min="0"
                        max="120"
                        className={cn("w-16 h-9", validationErrors.age && "border-destructive")}
                    />
                    <Select value="Years" onValueChange={() => {}}>
                      <SelectTrigger className="w-20 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Years">Years</SelectItem>
                        <SelectItem value="Months">Months</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => updateFormData('date_of_birth', e.target.value)}
                        className="flex-1 h-9"
                    />
                  </div>
                </div>
              </div>
              {validationErrors.age && (
                  <p className="text-xs text-destructive -mt-2 ml-[7.5rem]">{validationErrors.age}</p>
              )}

              {/* Row 3: City and Language */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <Label className="text-sm font-medium text-foreground">City</Label>
                  </div>
                  <Input
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      placeholder="Enter city"
                      className="flex-1 h-9"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <Label className="text-sm font-medium text-foreground">Language</Label>
                  </div>
                  <Select value={formData.preferred_language} onValueChange={(value) => updateFormData('preferred_language', value)}>
                    <SelectTrigger className="flex-1 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Hindi">Hindi</SelectItem>
                      <SelectItem value="Marathi">Marathi</SelectItem>
                      <SelectItem value="Gujarati">Gujarati</SelectItem>
                      <SelectItem value="Tamil">Tamil</SelectItem>
                      <SelectItem value="Telugu">Telugu</SelectItem>
                      <SelectItem value="Kannada">Kannada</SelectItem>
                      <SelectItem value="Malayalam">Malayalam</SelectItem>
                      <SelectItem value="Bengali">Bengali</SelectItem>
                      <SelectItem value="Punjabi">Punjabi</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 4: Pin Code and Blood Group */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <Label className="text-sm font-medium text-foreground">Pin Code</Label>
                  </div>
                  <div className="flex-1">
                    <Input
                        value={formData.pin_code}
                        onChange={(e) => updateFormData('pin_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit PIN"
                        maxLength={6}
                        className={cn("h-9", validationErrors.pin_code && "border-destructive")}
                    />
                    {validationErrors.pin_code && (
                        <p className="text-xs text-destructive mt-1">{validationErrors.pin_code}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                    <Heart className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <Label className="text-sm font-medium text-foreground">Blood</Label>
                  </div>
                  <Select value={formData.blood_group} onValueChange={(value) => updateFormData('blood_group', value)}>
                    <SelectTrigger className="flex-1 h-9">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A+">A+</SelectItem>
                      <SelectItem value="A-">A-</SelectItem>
                      <SelectItem value="B+">B+</SelectItem>
                      <SelectItem value="B-">B-</SelectItem>
                      <SelectItem value="AB+">AB+</SelectItem>
                      <SelectItem value="AB-">AB-</SelectItem>
                      <SelectItem value="O+">O+</SelectItem>
                      <SelectItem value="O-">O-</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 5: Address */}
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 min-w-[6rem] pt-2">
                  <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <Label className="text-sm font-medium text-foreground">Address</Label>
                </div>
                <Textarea
                    value={formData.address}
                    onChange={(e) => updateFormData('address', e.target.value)}
                    placeholder="Complete address"
                    rows={2}
                    className="resize-none flex-1"
                />
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-background flex-shrink-0">
            <div className="text-xs text-muted-foreground">
              {mode === 'edit' ? (
                  canSubmit ? "Ready to update" : "Fill required fields"
              ) : (
                  checkingDuplicates ? "Checking..." :
                      duplicateCheck?.has_duplicates && !userConfirmedDuplicate ? "Confirm if different person" :
                          canSubmit ? "Ready to save" : "Fill required fields"
              )}
            </div>
          <div className="flex items-center gap-3">
              <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={loading}
                  className={cn(
                      "px-6 medical-button-secondary",
                      compact && "px-4 text-sm h-9"
                  )}
              >
                Cancel
              </Button>

              {mode === 'edit' ? (
                  <Button
                      onClick={() => handleSubmit('patient-only')}
                      disabled={!canSubmit}
                      className="flex items-center gap-2 px-4 h-9"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Update
                  </Button>
              ) : (
                  <>
                    <Button
                        onClick={() => handleSubmit('patient-only')}
                        disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                        variant="outline"
                        className={cn(
                            "px-6 medical-button-primary",
                            compact && "px-4 text-sm h-9"
                        )}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                      Create
                    </Button>

                    {showPrescriptionOption && isDoctor && !showQueueOption && (
                        <Button
                            onClick={() => handleSubmit('patient-and-rx')}
                            disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                            className="px-4 h-9 bg-medical-schedule hover:bg-medical-schedule/90"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
                          Create & Rx
                        </Button>
                    )}
                    {showQueueOption && mode === 'create' && (
                      <Button
                        onClick={() => handleSubmit('patient-and-queue')}
                        disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                        className="px-4 h-9 bg-success hover:bg-success/90 text-white"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                        Create & Add to Queue
                      </Button>
                    )}
                  </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
} 